import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { setAuth, clearAuth, getAuth } from '../Auth/authState';
import type { User } from '../../MedicareProAPI/Entities/User/User';
import {
  AgencyRepository,
  UserRepository,
  UserTokenRepository,
  WebAPI,
} from '../../MedicareProAPI';
import type { Agency } from '../../MedicareProAPI/Entities/Agency/Agency';

export type AuthContextType = {
  getUser: () => User | null;
  getAgency: () => Agency | null;
  getAccessToken: () => string | null;
  isAuthenticated: () => boolean;
  isLoading: () => boolean;
  loginWithTokens: (t: { accessToken: string; expiry: Date }) => Promise<User>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const userRepository = new UserRepository();
const agencyRepository = new AgencyRepository();
const userTokenRepository = new UserTokenRepository();

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [agency, setAgency] = useState<Agency | null>(null);
  const [loading, setLoading] = useState(true);

  function getUser(): User | null {
    return user;
  }

  function getAgency(): Agency | null {
    return agency;
  }

  function getAccessToken(): string | null {
    return accessToken;
  }

  function isAuthenticated(): boolean {
    return !!user;
  }

  function isLoading(): boolean {
    return loading;
  }

  const loginWithTokens = ({
    accessToken,
    expiry,
  }: {
    accessToken: string;
    expiry: Date;
  }): Promise<User> => {
    setLoading(true);
    setAuth(accessToken, expiry);

    userRepository.setAccessToken(accessToken);
    agencyRepository.setAccessToken(accessToken);

    setAccessToken(accessToken);

    return userRepository.getMe({ include: ['profilePic', 'companyLogo'] }).then(async (user) => {
      try {
        const agency = await agencyRepository.getUserAgency(user.getUserId());
        setAgency(agency);
      } catch (e) {
        // User doesn't have an agency
      }

      setUser(user);
      setLoading(false);

      return user;
    });
  };

  async function logout() {
    try {
      await userTokenRepository.oAuthLogOut();
    } catch {
      /* ignore */
    }
    clearAuth();
    setUser(null);
  }

  // Bootstrap on app load: get a fresh access token + user via refresh cookie
  useEffect(() => {
    (async () => {
      try {
        const token = getAuth();

        if (!token) {
          throw new Error('No auth credentials');
        }

        loginWithTokens({ accessToken: token.accessToken, expiry: token.expiry });
      } catch {
        setLoading(false);
      }
    })();
  }, []);

  const value = useMemo<AuthContextType>(
    () => ({
      getUser,
      getAgency,
      getAccessToken,
      isAuthenticated,
      isLoading,
      loginWithTokens,
      logout,
    }),
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
